const privacyPolicyContent = {
  en: {
    title: "Urba App Privacy Policy",
    meta: {
      lastUpdated: "Last updated:",
      lastUpdatedDate: "May 15, 2026",
      serviceProvider: "Service provider:",
      serviceProviderValue: "Urba Driver LLC, doing business as Urba App",
      website: "Website:",
      contact: "Contact:",
      address: "Address:",
      addressValue: "734 Ramona Ave, Monterey, CA 93940, United States",
    },
    intro:
      "This Privacy Policy explains how Urba App collects, uses, stores, shares, and protects the personal information of riders, drivers, and visitors to our website. By using our app or services, you agree to the practices described in this policy.",
    sections: [
      {
        id: "1",
        title: "1. Who We Are",
        paragraphs: [
          "Urba App is a transportation app that connects riders and drivers to request and complete taxi-like or ridesharing trips. To provide this service, we need to collect certain personal and sensitive information, especially location data and driver documentation.",
        ],
      },
      {
        id: "2",
        title: "2. Information We Collect",
        paragraphs: [
          "We may collect the following categories of information depending on how you use the app:",
        ],
      },
      {
        id: "2.1",
        title: "2.1 Information You Provide Directly",
        items: [
          "Full name",
          "Phone number",
          "Email address",
          "Profile photo",
          'Saved addresses, frequent addresses, or "trusted addresses"',
          "Account and profile information",
          "Messages and communications you send us",
          "Support or complaint information",
          "Information you enter when requesting a trip, creating an account, or updating your profile",
        ],
      },
      {
        id: "2.2",
        title: "2.2 Location Information",
        listIntro: "We may collect:",
        items: [
          "Approximate location",
          "Precise or real-time location, when required for the service and when you grant the relevant permission",
          "Location associated with a trip, route, pickup, destination, estimated arrival time, and trip events",
        ],
        paragraphs: [
          "If you enable trip, tracking, safety, or assistance features during a ride, location may be collected while the app is in use and, if you expressly authorize it, during the trip or in the background to help provide the service, improve safety, and complete the request.",
        ],
      },
      {
        id: "2.3",
        title: "2.3 Driver Information",
        listIntro: "If you are a driver, we may also collect:",
        items: [
          "Driver's license",
          "Driver's license information or image",
          "Vehicle information",
          "Vehicle insurance or proof of insurance",
          "Vehicle inspection",
          "License plate, model, year, and other vehicle details",
          "Documents or verifications required by law or by the app",
          "Background checks, verifications, or certifications when permitted by law or necessary for our safety processes",
        ],
      },
      {
        id: "2.4",
        title: "2.4 Technical and Usage Information",
        listIntro: "We may automatically collect:",
        items: [
          "IP address",
          "Device identifiers",
          "Operating system and app version",
          "Activity logs, errors, and diagnostics",
          "Dates, times, and duration of use",
          "Features viewed, clicks, and preferences within the app",
        ],
      },
      {
        id: "2.5",
        title: "2.5 Cookies and Similar Technologies",
        listIntro:
          "When you use our website, we may use cookies, pixels, SDKs, scripts, or similar technologies to:",
        items: [
          "Remember preferences",
          "Measure usage and performance",
          "Improve the experience",
          "Detect failures or abuse",
          "Analyze website traffic",
        ],
        paragraphs: [
          "You can manage cookies through your browser settings, although disabling them may affect some website features.",
        ],
      },
      {
        id: "2.6",
        title: "2.6 Photos and Files",
        listIntro: "We may collect photos or files you upload for:",
        items: [
          "Profile photo",
          "Identity verification",
          "Driver verification",
          "Claims, support, or safety",
        ],
      },
      {
        id: "3",
        title: "3. How We Use Information",
        listIntro: "We use the information we collect to:",
        items: [
          "Create and manage accounts",
          "Connect riders and drivers",
          "Show location, routes, pickups, and destinations",
          "Process trip requests",
          "Communicate with you about the service",
          "Verify driver identity and documentation",
          "Maintain the safety of riders, drivers, and the system",
          "Prevent fraud, abuse, and unauthorized use",
          "Provide technical support and customer service",
          "Comply with legal, regulatory, and contractual obligations",
          "Improve app experience, functionality, and performance",
        ],
        paragraphs: [
          "We do not use your data for purposes incompatible with those described in this policy without first notifying you and, when required, obtaining your consent.",
        ],
      },
      {
        id: "4",
        title: "4. Legal Basis for Processing",
        listIntro: "We process your data in order to:",
        items: [
          "Provide the service you request",
          "Comply with legal obligations",
          "Protect legitimate interests related to security, fraud prevention, and service operation",
          "Rely on your consent when required by law, for example to access precise location, photos, or notifications",
        ],
      },
      {
        id: "5",
        title: "5. How We Share Information",
        listIntro: "We may share information in the following situations:",
        items: [
          "With drivers and riders, as needed to complete a trip",
          "With service providers that help us operate the app, such as cloud hosting, technical support, analytics, messaging, identity verification, maps, geolocation, or communications",
          "With public authorities or third parties when required by law, a court order, or a valid investigation",
          "With legal, financial, or compliance advisors",
          "In the event of a merger, acquisition, reorganization, or asset sale, provided appropriate protections are maintained",
          "With your consent, when you expressly authorize us",
        ],
        paragraphs: ["We do not sell your personal data or sensitive data."],
      },
      {
        id: "6",
        title: "6. Permissions and Sensitive Data",
        paragraphs: [
          "Urba App may request device permissions for specific functions, for example:",
        ],
        items: ["Location", "Camera", "Photos and files", "Notifications"],
        paragraphsAfter: [
          "We only request these permissions when they are necessary for the app to function. You can revoke permissions from your device settings, although doing so may limit some features.",
        ],
      },
      {
        id: "7",
        title: "7. Information Security",
        listIntro:
          "We apply reasonable technical and organizational measures to protect personal information, including:",
        items: [
          "Secure transmission through HTTPS or other encryption-in-transit protocols",
          "Access controls",
          "Security monitoring",
          "System backups and protection",
          "Review of vendors with access to data, when applicable",
        ],
        paragraphs: [
          "However, no system is completely secure. We cannot guarantee absolute security.",
        ],
      },
      {
        id: "8",
        title: "8. Data Retention",
        listIntro: "We retain personal information only for as long as necessary to:",
        items: [
          "Provide the service",
          "Comply with legal, tax, security, or regulatory obligations",
          "Resolve disputes",
          "Enforce our agreements",
          "Prevent fraud or misuse",
        ],
        paragraphs: [
          "When we no longer need the data, we will delete or anonymize it securely, unless we must retain it for a longer period required by law or for legitimate security and compliance reasons.",
        ],
      },
      {
        id: "9",
        title: "9. Account and Data Deletion",
        paragraphs: [
          "If you created an account in Urba App, you may request deletion of your account and the data associated with it.",
          "To request deletion:",
        ],
        items: [
          "Email: urba@urbaapp.com",
          "Include the email address or phone number linked to your account",
          "State that you want to delete your account and personal data",
        ],
        paragraphsAfter: [
          "When we receive a valid request, we will delete the account and associated data, except for information we must keep for legal, security, fraud prevention, accounting, or regulatory compliance purposes. In that case, we will keep only the minimum necessary information for the strictly required period.",
        ],
      },
      {
        id: "10",
        title: "10. Your Rights",
        listIntro: "Depending on your country or state, you may have the right to:",
        items: [
          "Access your personal data",
          "Correct inaccurate information",
          "Request deletion",
          "Object to certain processing",
          "Withdraw consent where permitted by law",
          "Request data portability, where applicable",
        ],
        rightsContactPrefix: "To exercise your rights, write to us at ",
        rightsContactSuffix: ".",
      },
      {
        id: "11",
        title: "11. Children's Privacy",
        paragraphs: [
          "Urba App is not directed to children under 13, and we do not knowingly collect personal data from children under that age. If you believe a child has provided us with personal information, contact us so we can review it and, if appropriate, delete it.",
        ],
      },
      {
        id: "12",
        title: "12. International Transfers",
        paragraphs: [
          "Your information may be processed and stored on servers located outside your state, country, or jurisdiction. When we make international transfers, we take reasonable measures to protect the information in accordance with this policy and applicable law.",
        ],
      },
      {
        id: "13",
        title: "13. Third-Party Links and Services",
        paragraphs: [
          "The app or website may include links or integrations with third parties, such as maps, messaging, analytics, or payment providers. Those third parties have their own privacy policies, and we encourage you to review them. We are not responsible for their practices outside our control.",
        ],
      },
      {
        id: "14",
        title: "14. Changes to This Policy",
        paragraphs: [
          'We may update this Privacy Policy from time to time. When we do, we will post the new version on this page, update the "Last updated" date, and, if the change is material, we may notify you in the app or by email.',
        ],
      },
      {
        id: "15",
        title: "15. Contact Us",
        paragraphs: [
          "If you have questions, privacy requests, or want to exercise your rights, you can contact us at:",
        ],
        contactBlock: {
          company: "Urba Driver LLC d/b/a Urba App",
          address: "734 Ramona Ave, Monterey, CA 93940, United States",
          emailLabel: "Email:",
          phoneLabel: "Phone:",
        },
      },
    ],
    disclosure: {
      title: "In-app disclosure and consent",
      text: "Urba App collects your location, photo, name, phone number, and other data needed to connect riders and drivers and complete trips. If you are a driver, we also collect your driver's license, insurance, and vehicle inspection information to verify your eligibility.",
    },
  },
  es: {
    title: "Política de privacidad de Urba App",
    meta: {
      lastUpdated: "Última actualización:",
      lastUpdatedDate: "15 de mayo de 2026",
      serviceProvider: "Proveedor del servicio:",
      serviceProviderValue: "Urba Driver LLC, operando como Urba App",
      website: "Sitio web:",
      contact: "Contacto:",
      address: "Dirección:",
      addressValue: "734 Ramona Ave, Monterey, CA 93940, Estados Unidos",
    },
    intro:
      "Esta Política de Privacidad explica cómo Urba App recopila, utiliza, almacena, comparte y protege la información personal de pasajeros, conductores y visitantes de nuestro sitio web. Al usar nuestra aplicación o servicios, aceptas las prácticas descritas en esta política.",
    sections: [
      {
        id: "1",
        title: "1. Quiénes somos",
        paragraphs: [
          "Urba App es una aplicación de transporte que conecta pasajeros y conductores para solicitar y completar viajes tipo taxi o ridesharing. Para ofrecer este servicio, necesitamos recopilar cierta información personal y sensible, especialmente datos de ubicación y documentación del conductor.",
        ],
      },
      {
        id: "2",
        title: "2. Información que recopilamos",
        paragraphs: [
          "Podemos recopilar las siguientes categorías de información según cómo uses la aplicación:",
        ],
      },
      {
        id: "2.1",
        title: "2.1 Información que proporcionas directamente",
        items: [
          "Nombre completo",
          "Número de teléfono",
          "Correo electrónico",
          "Foto de perfil",
          'Direcciones guardadas, direcciones frecuentes o direcciones "de confianza"',
          "Información de cuenta y perfil",
          "Mensajes y comunicaciones que nos envías",
          "Información de soporte o reclamaciones",
          "Información que ingresas al solicitar un viaje, crear una cuenta o actualizar tu perfil",
        ],
      },
      {
        id: "2.2",
        title: "2.2 Información de ubicación",
        listIntro: "Podemos recopilar:",
        items: [
          "Ubicación aproximada",
          "Ubicación precisa o en tiempo real, cuando sea necesaria para el servicio y cuando otorgues el permiso correspondiente",
          "Ubicación asociada a un viaje, ruta, punto de recogida, destino, hora estimada de llegada y eventos del viaje",
        ],
        paragraphs: [
          "Si activas funciones de viaje, seguimiento, seguridad o asistencia durante un trayecto, la ubicación puede recopilarse mientras usas la aplicación y, si lo autorizas expresamente, durante el viaje o en segundo plano para ayudar a prestar el servicio, mejorar la seguridad y completar la solicitud.",
        ],
      },
      {
        id: "2.3",
        title: "2.3 Información del conductor",
        listIntro: "Si eres conductor, también podemos recopilar:",
        items: [
          "Licencia de conducir",
          "Información o imagen de la licencia de conducir",
          "Información del vehículo",
          "Seguro del vehículo o comprobante de seguro",
          "Inspección del vehículo",
          "Placa, modelo, año y otros datos del vehículo",
          "Documentos o verificaciones exigidos por ley o por la aplicación",
          "Verificaciones de antecedentes, certificaciones o comprobaciones cuando la ley lo permita o sean necesarias para nuestros procesos de seguridad",
        ],
      },
      {
        id: "2.4",
        title: "2.4 Información técnica y de uso",
        listIntro: "Podemos recopilar automáticamente:",
        items: [
          "Dirección IP",
          "Identificadores del dispositivo",
          "Sistema operativo y versión de la aplicación",
          "Registros de actividad, errores y diagnósticos",
          "Fechas, horas y duración de uso",
          "Funciones consultadas, clics y preferencias dentro de la aplicación",
        ],
      },
      {
        id: "2.5",
        title: "2.5 Cookies y tecnologías similares",
        listIntro:
          "Cuando usas nuestro sitio web, podemos utilizar cookies, píxeles, SDK, scripts u otras tecnologías similares para:",
        items: [
          "Recordar preferencias",
          "Medir uso y rendimiento",
          "Mejorar la experiencia",
          "Detectar fallos o abusos",
          "Analizar el tráfico del sitio web",
        ],
        paragraphs: [
          "Puedes gestionar las cookies desde la configuración de tu navegador, aunque desactivarlas puede afectar algunas funciones del sitio.",
        ],
      },
      {
        id: "2.6",
        title: "2.6 Fotos y archivos",
        listIntro: "Podemos recopilar fotos o archivos que subas para:",
        items: [
          "Foto de perfil",
          "Verificación de identidad",
          "Verificación del conductor",
          "Reclamaciones, soporte o seguridad",
        ],
      },
      {
        id: "3",
        title: "3. Cómo usamos la información",
        listIntro: "Usamos la información que recopilamos para:",
        items: [
          "Crear y administrar cuentas",
          "Conectar pasajeros y conductores",
          "Mostrar ubicación, rutas, recogidas y destinos",
          "Procesar solicitudes de viaje",
          "Comunicarnos contigo sobre el servicio",
          "Verificar la identidad y documentación del conductor",
          "Mantener la seguridad de pasajeros, conductores y del sistema",
          "Prevenir fraude, abusos y usos no autorizados",
          "Brindar soporte técnico y atención al cliente",
          "Cumplir obligaciones legales, regulatorias y contractuales",
          "Mejorar la experiencia, funcionalidad y rendimiento de la aplicación",
        ],
        paragraphs: [
          "No usamos tus datos para fines incompatibles con los descritos en esta política sin notificarte antes y, cuando sea necesario, obtener tu consentimiento.",
        ],
      },
      {
        id: "4",
        title: "4. Base legal del tratamiento",
        listIntro: "Tratamos tus datos para:",
        items: [
          "Prestar el servicio que solicitas",
          "Cumplir obligaciones legales",
          "Proteger intereses legítimos relacionados con la seguridad, prevención del fraude y operación del servicio",
          "Basarnos en tu consentimiento cuando la ley lo exija, por ejemplo para acceder a ubicación precisa, fotos o notificaciones",
        ],
      },
      {
        id: "5",
        title: "5. Cómo compartimos la información",
        listIntro: "Podemos compartir información en los siguientes casos:",
        items: [
          "Con conductores y pasajeros, según sea necesario para completar un viaje",
          "Con proveedores de servicios que nos ayudan a operar la aplicación, como alojamiento en la nube, soporte técnico, analítica, mensajería, verificación de identidad, mapas, geolocalización o comunicaciones",
          "Con autoridades públicas o terceros cuando lo exija la ley, una orden judicial o una investigación válida",
          "Con asesores legales, financieros o de cumplimiento",
          "En caso de fusión, adquisición, reorganización o venta de activos, manteniendo las protecciones adecuadas",
          "Con tu consentimiento, cuando nos autorices expresamente",
        ],
        paragraphs: [
          "No vendemos tus datos personales ni datos sensibles.",
        ],
      },
      {
        id: "6",
        title: "6. Permisos y datos sensibles",
        paragraphs: [
          "Urba App puede solicitar permisos del dispositivo para funciones específicas, por ejemplo:",
        ],
        items: ["Ubicación", "Cámara", "Fotos y archivos", "Notificaciones"],
        paragraphsAfter: [
          "Solo solicitamos estos permisos cuando son necesarios para el funcionamiento de la aplicación. Puedes revocarlos desde la configuración de tu dispositivo, aunque hacerlo puede limitar algunas funciones.",
        ],
      },
      {
        id: "7",
        title: "7. Seguridad de la información",
        listIntro:
          "Aplicamos medidas técnicas y organizativas razonables para proteger la información personal, incluyendo:",
        items: [
          "Transmisión segura mediante HTTPS u otros protocolos de cifrado en tránsito",
          "Controles de acceso",
          "Monitoreo de seguridad",
          "Copias de seguridad y protección de sistemas",
          "Revisión de proveedores con acceso a datos, cuando corresponda",
        ],
        paragraphs: [
          "Sin embargo, ningún sistema es completamente seguro. No podemos garantizar seguridad absoluta.",
        ],
      },
      {
        id: "8",
        title: "8. Conservación de datos",
        listIntro:
          "Conservamos la información personal solo el tiempo necesario para:",
        items: [
          "Prestar el servicio",
          "Cumplir obligaciones legales, fiscales, de seguridad o regulatorias",
          "Resolver disputas",
          "Hacer cumplir nuestros acuerdos",
          "Prevenir fraude o uso indebido",
        ],
        paragraphs: [
          "Cuando ya no necesitemos los datos, los eliminaremos o anonimizaremos de forma segura, salvo que debamos conservarlos por un período mayor exigido por ley o por motivos legítimos de seguridad y cumplimiento.",
        ],
      },
      {
        id: "9",
        title: "9. Eliminación de cuenta y datos",
        paragraphs: [
          "Si creaste una cuenta en Urba App, puedes solicitar la eliminación de tu cuenta y los datos asociados.",
          "Para solicitar la eliminación:",
        ],
        items: [
          "Correo: urba@urbaapp.com",
          "Incluye el correo electrónico o número de teléfono vinculado a tu cuenta",
          "Indica que deseas eliminar tu cuenta y tus datos personales",
        ],
        paragraphsAfter: [
          "Al recibir una solicitud válida, eliminaremos la cuenta y los datos asociados, excepto la información que debamos conservar por motivos legales, de seguridad, prevención del fraude, contabilidad o cumplimiento normativo. En ese caso, conservaremos solo la información mínima necesaria durante el período estrictamente requerido.",
        ],
      },
      {
        id: "10",
        title: "10. Tus derechos",
        listIntro:
          "Según tu país o estado, puedes tener derecho a:",
        items: [
          "Acceder a tus datos personales",
          "Corregir información inexacta",
          "Solicitar la eliminación",
          "Oponerte a ciertos tratamientos",
          "Retirar el consentimiento cuando la ley lo permita",
          "Solicitar la portabilidad de datos, cuando corresponda",
        ],
        rightsContactPrefix: "Para ejercer tus derechos, escríbenos a ",
        rightsContactSuffix: ".",
      },
      {
        id: "11",
        title: "11. Privacidad de menores",
        paragraphs: [
          "Urba App no está dirigida a menores de 13 años y no recopilamos a sabiendas datos personales de menores de esa edad. Si crees que un menor nos ha proporcionado información personal, contáctanos para revisarla y, si corresponde, eliminarla.",
        ],
      },
      {
        id: "12",
        title: "12. Transferencias internacionales",
        paragraphs: [
          "Tu información puede procesarse y almacenarse en servidores ubicados fuera de tu estado, país o jurisdicción. Cuando realizamos transferencias internacionales, adoptamos medidas razonables para proteger la información de acuerdo con esta política y la ley aplicable.",
        ],
      },
      {
        id: "13",
        title: "13. Enlaces y servicios de terceros",
        paragraphs: [
          "La aplicación o el sitio web pueden incluir enlaces o integraciones con terceros, como mapas, mensajería, analítica o proveedores de pago. Esos terceros tienen sus propias políticas de privacidad; te recomendamos revisarlas. No somos responsables de sus prácticas fuera de nuestro control.",
        ],
      },
      {
        id: "14",
        title: "14. Cambios en esta política",
        paragraphs: [
          'Podemos actualizar esta Política de Privacidad periódicamente. Cuando lo hagamos, publicaremos la nueva versión en esta página, actualizaremos la fecha de "Última actualización" y, si el cambio es sustancial, podremos notificarte en la aplicación o por correo electrónico.',
        ],
      },
      {
        id: "15",
        title: "15. Contáctanos",
        paragraphs: [
          "Si tienes preguntas, solicitudes de privacidad o deseas ejercer tus derechos, puedes contactarnos en:",
        ],
        contactBlock: {
          company: "Urba Driver LLC d/b/a Urba App",
          address: "734 Ramona Ave, Monterey, CA 93940, Estados Unidos",
          emailLabel: "Correo:",
          phoneLabel: "Teléfono:",
        },
      },
    ],
    disclosure: {
      title: "Divulgación y consentimiento en la aplicación",
      text: "Urba App recopila tu ubicación, foto, nombre, número de teléfono y otros datos necesarios para conectar pasajeros y conductores y completar viajes. Si eres conductor, también recopilamos tu licencia de conducir, seguro e información de inspección del vehículo para verificar tu elegibilidad.",
    },
  },
};

export function getPrivacyPolicyLocale(language) {
  if (language && String(language).toLowerCase().startsWith("es")) {
    return "es";
  }
  return "en";
}

export function getPrivacyPolicyContent(language) {
  const locale = getPrivacyPolicyLocale(language);
  return privacyPolicyContent[locale];
}

export default privacyPolicyContent;
